import React, { useEffect, useMemo, useRef, useState } from "react";

// SEC-08: render admin-pasted embed HTML (Cognito Forms, HubSpot, Typeform, etc.)
// inside a SANDBOXED iframe via `srcdoc`. The sandbox deliberately OMITS
// `allow-same-origin` (and `allow-top-navigation`), so the embed runs in a
// null/opaque origin: scripts/forms/popups work, but the embed CANNOT read the
// parent's cookies, localStorage/sessionStorage, the same-origin /api, or the
// parent DOM, and cannot top-navigate the page. Do NOT add `allow-same-origin`.
const SANDBOX = "allow-scripts allow-forms allow-popups";

// Load the SAME brand font the site uses. Loading external CSS/fonts inside the
// opaque-origin sandbox is allowed and does NOT weaken it — we never add
// allow-same-origin / allow-top-navigation.
const FONT_HEAD =
  '<link rel="preconnect" href="https://fonts.googleapis.com">' +
  '<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>' +
  '<link href="https://fonts.googleapis.com/css2?family=Google+Sans+Flex:opsz,wght@8..144,300..800&display=swap" rel="stylesheet">';

// Startup Otter brand theming for embedded Cognito Forms. Modern Cognito (v2)
// renders a HYBRID DOM: `.cog-*` wrappers + Element-UI `.el-*` controls (the REAL
// editable elements are `.el-input__inner` / `.el-textarea__inner`, NOT `.cog-input`,
// which is only a wrapper). Cognito also ships with `cog-cognito--protect-css`, which
// injects its own stylesheet AFTER ours — so we (a) scope everything under
// `.cog-cognito` and use `!important` for higher specificity, and (b) keep this <style>
// pinned LAST in the document (see the pin observer in buildSrcDoc) so Cognito's CSS
// can't win on document order. Brand values mirror the site's light-theme tokens
// (teal #148b8f, fg #0b1220, muted #5b667a, border #dce6e7, Google Sans Flex).
const FORM_THEME_CSS = `
  :root{--so-primary:#148b8f;--so-primary-hover:#0f6f72;--so-fg:#0b1220;--so-muted:#5b667a;--so-border:#dce6e7;--so-radius:10px;--so-font:"Google Sans Flex",ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;}
  html,body{margin:0 !important;padding:0 !important;background:transparent !important;}
  .cog-cognito,.cog-cognito *{font-family:var(--so-font) !important;box-sizing:border-box;}
  body,input,textarea,select,button,label{font-family:var(--so-font) !important;color:var(--so-fg);}
  /* Field labels + required marker */
  .cog-cognito .cog-label,.cog-cognito label{font-weight:560 !important;font-size:14px !important;color:var(--so-fg) !important;letter-spacing:-0.003em;}
  .cog-cognito .cog-label__req,.cog-cognito .cog-required,.cog-cognito .cog-label sup,.cog-cognito .cog-required-marker{color:var(--so-primary) !important;}
  /* Help text */
  .cog-cognito .cog-helptext,.cog-cognito .cog-help-text,.cog-cognito .cog-field__help,.cog-cognito .cog-help{color:var(--so-muted) !important;font-size:12.5px !important;}
  /* Text inputs / textareas / select trigger (the real Element-UI inner elements) */
  .cog-cognito .el-input__inner,.cog-cognito .el-textarea__inner,.cog-cognito input.cog-input,.cog-cognito textarea.cog-input{font-family:var(--so-font) !important;font-size:14px !important;color:var(--so-fg) !important;background:#fff !important;border:1px solid var(--so-border) !important;border-radius:var(--so-radius) !important;box-shadow:0 1px 2px rgba(16,24,40,.04) !important;line-height:1.45 !important;transition:border-color .15s ease,box-shadow .15s ease !important;}
  .cog-cognito .el-input__inner{height:42px !important;padding:0 12px !important;}
  .cog-cognito .el-textarea__inner{min-height:104px !important;padding:10px 12px !important;}
  .cog-cognito .el-input__inner:focus,.cog-cognito .el-textarea__inner:focus,.cog-cognito .el-input.is-focus .el-input__inner{outline:none !important;border-color:var(--so-primary) !important;box-shadow:0 0 0 3px rgba(20,139,143,.18) !important;}
  .cog-cognito ::placeholder{color:var(--so-muted) !important;opacity:1 !important;}
  /* Select dropdown options */
  .el-select-dropdown__item.selected,.el-select-dropdown__item.hover,.el-select-dropdown__item:hover{color:var(--so-primary) !important;}
  .el-select-dropdown__item.selected{font-weight:600 !important;}
  /* Radio / checkbox (Element-UI) */
  .cog-cognito .el-radio__inner,.cog-cognito .el-checkbox__inner{border-color:var(--so-border) !important;}
  .cog-cognito .el-radio.is-checked .el-radio__inner,.cog-cognito .el-checkbox.is-checked .el-checkbox__inner{background:var(--so-primary) !important;border-color:var(--so-primary) !important;}
  .cog-cognito .el-radio.is-checked .el-radio__inner::after{background:#fff !important;}
  .cog-cognito .el-radio__label,.cog-cognito .el-checkbox__label,.cog-cognito .el-radio.is-checked .el-radio__label,.cog-cognito .el-checkbox.is-checked .el-checkbox__label{color:var(--so-fg) !important;font-weight:420 !important;}
  /* Headings / section titles + rich-text body */
  .cog-cognito h1,.cog-cognito h2,.cog-cognito h3,.cog-cognito h4,.cog-cognito .cog-section__title,.cog-cognito .cog-html h1,.cog-cognito .cog-html h2,.cog-cognito .cog-html h3{color:var(--so-fg) !important;font-weight:680 !important;letter-spacing:-0.01em !important;}
  .cog-cognito .cog-html,.cog-cognito .cog-html p,.cog-cognito p{color:var(--so-fg) !important;}
  .cog-cognito a,.cog-cognito .cog-html a{color:var(--so-primary) !important;}
  /* Errors */
  .cog-cognito .cog-error,.cog-cognito .cog-error-message,.cog-cognito .el-form-item__error{color:#d64545 !important;font-size:12.5px !important;}
  .cog-cognito .is-error .el-input__inner,.cog-cognito .is-error .el-textarea__inner{border-color:#d64545 !important;}
  /* Buttons (submit / navigation) */
  .cog-cognito .cog-button,.cog-cognito .el-button,.cog-cognito button,.cog-cognito input[type=submit]{font-family:var(--so-font) !important;font-size:14px !important;font-weight:600 !important;border-radius:var(--so-radius) !important;padding:10px 20px !important;cursor:pointer !important;border:1px solid transparent !important;transition:background-color .15s ease,opacity .15s ease !important;}
  .cog-cognito .cog-button--primary,.cog-cognito .cog-button--submit,.cog-cognito .el-button--primary,.cog-cognito button[type=submit]{background:var(--so-primary) !important;color:#fff !important;border-color:var(--so-primary) !important;box-shadow:0 1px 2px rgba(16,24,40,.08) !important;}
  .cog-cognito .cog-button--primary:hover,.cog-cognito .cog-button--submit:hover,.cog-cognito .el-button--primary:hover{background:var(--so-primary-hover) !important;border-color:var(--so-primary-hover) !important;}
  .cog-cognito .cog-button--secondary{background:#fff !important;color:var(--so-fg) !important;border-color:var(--so-border) !important;}
  .cog-cognito .cog-button--secondary:hover{background:#f1f6f6 !important;}
  /* Progress bar (multi-page forms) */
  .cog-cognito .cog-progressbar__filler,.cog-cognito .cog-progress-bar__filler{background:var(--so-primary) !important;}
  /* Post-submit confirmation message */
  .cog-cognito .cog-confirmation,.cog-cognito .cog-form__confirmation,.cog-cognito .cog-confirmation-message,.cog-cognito .cog-confirmation *{font-family:var(--so-font) !important;color:var(--so-fg) !important;}
  .cog-cognito .cog-confirmation{font-size:15px !important;line-height:1.6 !important;}
  .cog-cognito .cog-confirmation h1,.cog-cognito .cog-confirmation h2,.cog-cognito .cog-confirmation h3{color:var(--so-fg) !important;font-weight:680 !important;letter-spacing:-0.01em !important;}
  .cog-cognito .cog-confirmation a{color:var(--so-primary) !important;}
`;

function buildSrcDoc(html) {
  return (
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width, initial-scale=1">` +
    FONT_HEAD +
    `<style id="so-embed-theme">${FORM_THEME_CSS}</style></head><body>` +
    (html || "") +
    // Height reporter: relays content height to the parent via postMessage so the
    // outer iframe can grow to fit the form (cross-origin postMessage works even
    // from an opaque-origin sandbox; storage access does not — by design).
    `<script>(function(){` +
    // Swallow the embed's own errors INSIDE the iframe so an opaque-origin error
    // doesn't bubble to the parent as a cross-origin "Script error." (which the
    // CRA dev overlay mishandles). Does not weaken the sandbox.
    `try{window.addEventListener('error',function(e){e.preventDefault();},true);window.addEventListener('unhandledrejection',function(e){e.preventDefault();});}catch(_){}` +
    // Keep our brand <style> pinned LAST so Cognito's late `protect-css` stylesheet
    // (injected after load) can't override us on document order. A <style> has no
    // rendered box, so moving it never affects the body height measurement below.
    `function pin(){try{var s=document.getElementById('so-embed-theme');var b=document.body;if(s&&b&&b.lastElementChild!==s){b.appendChild(s);}}catch(e){}}` +
    `pin();document.addEventListener('DOMContentLoaded',pin);window.addEventListener('load',pin);` +
    `try{var mo=new MutationObserver(function(m){for(var i=0;i<m.length;i++){var a=m[i].addedNodes;for(var j=0;j<a.length;j++){var n=a[j];if(n.nodeType===1&&n.id!=='so-embed-theme'&&(n.tagName==='STYLE'||n.tagName==='LINK')){pin();return;}}}});mo.observe(document.documentElement,{childList:true,subtree:true});}catch(e){}` +
    `var pc=0,pv=setInterval(function(){pin();if(++pc>20)clearInterval(pv);},300);` +
    // Measure the BODY content height only. Never read documentElement.scrollHeight:
    // the <html> element stretches to the iframe viewport, so reading it creates a
    // feedback loop (parent grows iframe -> viewport grows -> reported height grows
    // -> parent grows again) that runs away to the clamp. body height is content-driven.
    `var last=0;function measure(){var b=document.body;if(!b)return 0;` +
    `var r=b.getBoundingClientRect();return Math.ceil(Math.max(b.scrollHeight,b.offsetHeight,r.height));}` +
    `function h(){try{var n=measure();if(n>0&&Math.abs(n-last)>2){last=n;parent.postMessage({__soEmbedHeight:n},'*');}}catch(e){}}` +
    `window.addEventListener('load',h);document.addEventListener('DOMContentLoaded',h);` +
    `if(window.ResizeObserver){try{new ResizeObserver(h).observe(document.body);}catch(e){}}` +
    `setInterval(h,1000);h();})();<\/script>` +
    `</body></html>`
  );
}

export function SandboxedEmbed({ html, title = "Embedded form", minHeight = 520, className }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(minHeight);
  const srcDoc = useMemo(() => buildSrcDoc(html), [html]);

  useEffect(() => {
    function onMsg(e) {
      if (ref.current && e.source === ref.current.contentWindow) {
        const v = e.data && e.data.__soEmbedHeight;
        if (typeof v === "number" && v > 0) {
          const next = Math.min(8000, Math.max(minHeight, Math.ceil(v) + 4));
          // Hysteresis guard: ignore sub-pixel jitter so the iframe never oscillates/grows.
          setHeight((cur) => (Math.abs(next - cur) > 4 ? next : cur));
        }
      }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [minHeight]);

  // Reset height when the embed content changes so a smaller embed doesn't keep a stale tall height.
  useEffect(() => { setHeight(minHeight); }, [srcDoc, minHeight]);

  if (!html || !html.trim()) return null;

  return (
    <iframe
      ref={ref}
      title={title}
      srcDoc={srcDoc}
      sandbox={SANDBOX}
      referrerPolicy="strict-origin-when-cross-origin"
      loading="lazy"
      className={className}
      style={{ width: "100%", height, border: 0, display: "block" }}
      data-testid="sandboxed-embed-iframe"
    />
  );
}

export const EMBED_TRUST_WARNING =
  "Paste trusted embed code only. Startup Otter will run this embed inside a sandboxed frame (isolated from your account, cookies, and this page) to protect visitors and logged-in users. Cognito Forms is supported, but some advanced embed behaviors may require Cognito\u2019s iframe embed option.";
